import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import LocalPizzaIcon from '@mui/icons-material/LocalPizza';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { toast } from 'react-toastify';

import AuthContext from '../../context/authContext';
import SocketIOContext from '../../context/socketIOContext';
import useFoodOrderKitchen from '../../hooks/useFoodOrderKitchen';
import { attentionStripeBackground } from '../../util/attentionStripes';

dayjs.locale('de');

const STATUS_LABEL = {
  inPreparation: 'In Zubereitung',
  ready: 'Abholbereit',
};

// How long a freshly placed online order stays visually emphasised as "live" so
// kitchen staff immediately notice a new order among the running pizza bookings.
const FRESH_WINDOW_MS = 3 * 60 * 1000;

// Live board of online food orders ("Speisen"), embedded into the merged kitchen
// (formerly pizza) overview. Orders arrive in real time via Socket.io and can be
// advanced through the pickup flow. Newly ordered dishes are highlighted so they
// stand out and are directly recognisable as fresh live orders.
//
// Branch filtering: normally the parent kitchen view drives the location via the
// `branchId` prop. For users without pizza rights (food-only kitchen staff) the
// parent has no branch selector, so `selfManageBranch` makes the board render and
// manage its own location filter, derived from the incoming orders.
function KitchenOrdersBoard({ branchId = '', selfManageBranch = false }) {
  const authCtx = useContext(AuthContext);
  const { on, emit } = useContext(SocketIOContext);
  const { getKitchenOverview, updateOrderStatus } = useFoodOrderKitchen();
  const theme = useTheme();

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [ownBranchId, setOwnBranchId] = useState('');
  // orderId -> timestamp when this client first saw the order arrive live via socket.
  const [liveArrivals, setLiveArrivals] = useState({});

  const canUpdate = authCtx.checkRight('foodOrder:update');
  const effectiveBranchId = selfManageBranch ? ownBranchId : branchId;

  const load = async ({ showLoading = false } = {}) => {
    if (showLoading) setIsLoading(true);
    setLoadError(null);
    try {
      const data = await getKitchenOverview();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError(err?.message || 'Bestellungen konnten nicht geladen werden.');
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  // Initial load + 20s polling fallback in case a socket event is missed.
  useEffect(() => {
    load({ showLoading: true });
    const interval = setInterval(() => {
      setNowTs(Date.now());
      load({ showLoading: false });
    }, 20 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live updates: join the global food-order room and react to changes. New orders
  // are recorded as "live arrivals" so they can be highlighted for a short window.
  useEffect(() => {
    emit('join_food_orders', {});
    const unsubscribe = on('foodOrder:update', payload => {
      const order = payload?.order;
      if (!order?._id) return;
      const id = String(order._id);
      if (payload.type === 'new') {
        setLiveArrivals(prev => ({ ...prev, [id]: Date.now() }));
      }
      setOrders(prev => {
        const without = prev.filter(o => String(o._id) !== id);
        if (payload.type === 'removed') return without;
        return [...without, order];
      });
    });
    return () => unsubscribe && unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, emit]);

  // Location options for the self-managed selector, derived from the live orders.
  const branchOptions = useMemo(() => {
    if (!selfManageBranch) return [];
    const seen = new Map();
    orders.forEach(o => {
      if (o.branchId && !seen.has(String(o.branchId))) {
        seen.set(String(o.branchId), o.locationCode || String(o.branchId));
      }
    });
    return [{ id: '', label: 'Alle Standorte' }, ...[...seen].map(([id, label]) => ({ id, label }))];
  }, [orders, selfManageBranch]);

  const visibleOrders = useMemo(() => {
    return orders
      .filter(o => !effectiveBranchId || String(o.branchId) === String(effectiveBranchId))
      .slice()
      .sort((a, b) => new Date(a.orderedAt || 0) - new Date(b.orderedAt || 0));
  }, [orders, effectiveBranchId]);

  // An order counts as a fresh live order while it is still in preparation and was
  // either just seen arriving via socket or ordered within the freshness window.
  const isOrderFresh = order => {
    if (order.status !== 'inPreparation') return false;
    const arrival = liveArrivals[String(order._id)];
    if (arrival && nowTs - arrival < FRESH_WINDOW_MS) return true;
    const orderedAt = order.orderedAt ? new Date(order.orderedAt).getTime() : null;
    return !!orderedAt && nowTs - orderedAt < FRESH_WINDOW_MS;
  };

  const liveCount = visibleOrders.filter(isOrderFresh).length;

  const handleStatus = async (order, status) => {
    if (!canUpdate) return;
    setBusyId(String(order._id));
    try {
      await updateOrderStatus(order._id, status);
      // Optimistically remove/advance; the socket event will reconcile too.
      setOrders(prev => {
        const without = prev.filter(o => String(o._id) !== String(order._id));
        if (status === 'pickedUp' || status === 'cancelled') return without;
        return [...without, { ...order, status }];
      });
    } catch (err) {
      toast.error('Status konnte nicht geändert werden.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: '2px solid',
        borderColor: theme.palette.secondary.main,
        background: alpha(theme.palette.secondary.main, 0.05),
        // Defined once here; fresh order cards reference it by name below.
        '@keyframes kitchenLivePulse': {
          '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.55)}` },
          '70%': { boxShadow: `0 0 0 12px ${alpha(theme.palette.warning.main, 0)}` },
          '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0)}` },
        },
      }}
    >
      <Stack spacing={{ xs: 1.5, sm: 2 }}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
          <RestaurantIcon color='secondary' />
          <Typography variant='h6'>Bestellte Speisen</Typography>
          <Chip size='small' color='secondary' label='ONLINE · LIVE' />
          {liveCount > 0 && (
            <Chip size='small' color='warning' icon={<FiberNewIcon />} label={`${liveCount} neu`} />
          )}
          <Box sx={{ flexGrow: 1 }} />
          {selfManageBranch && branchOptions.length > 2 && (
            <FormControl size='small' sx={{ minWidth: 200 }}>
              <InputLabel id='kitchen-board-branch-label'>Standort</InputLabel>
              <Select
                labelId='kitchen-board-branch-label'
                label='Standort'
                value={ownBranchId}
                onChange={e => setOwnBranchId(e.target.value)}
              >
                {branchOptions.map(o => (
                  <MenuItem key={o.id || 'all'} value={o.id}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Typography variant='body2' color='text.secondary'>
            {visibleOrders.length} offen
          </Typography>
        </Stack>

        {!canUpdate && (
          <Alert severity='info'>
            Ansicht ohne Bearbeitungsrecht – der Status der Speisen-Bestellungen kann nicht geändert
            werden.
          </Alert>
        )}
        {loadError && <Alert severity='error'>{loadError}</Alert>}

        {isLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : visibleOrders.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
            Aktuell keine offenen Speisen-Bestellungen. Neue Online-Bestellungen erscheinen hier
            automatisch.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {visibleOrders.map(order => {
              const isReady = order.status === 'ready';
              const fresh = isOrderFresh(order);
              const minsAgo = order.orderedAt
                ? Math.max(0, Math.floor((nowTs - new Date(order.orderedAt).getTime()) / 60000))
                : null;
              const accentColor = fresh
                ? theme.palette.warning.main
                : isReady
                  ? theme.palette.success.main
                  : theme.palette.secondary.main;
              return (
                <Paper
                  key={order._id}
                  elevation={fresh ? 6 : 0}
                  sx={{
                    position: 'relative',
                    p: { xs: 1.5, sm: 2 },
                    border: fresh ? '2px solid' : '1px solid',
                    borderColor: fresh
                      ? theme.palette.warning.main
                      : isReady
                        ? theme.palette.success.main
                        : 'divider',
                    borderTop: '6px solid',
                    borderTopColor: accentColor,
                    background: fresh
                      ? attentionStripeBackground(theme, { color: 'warning' })
                      : order.hasPizza
                        ? alpha(theme.palette.secondary.main, 0.05)
                        : theme.palette.background.paper,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    ...(fresh && { animation: 'kitchenLivePulse 1.8s ease-out infinite' }),
                  }}
                >
                  <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography variant='h4' sx={{ fontWeight: 800, lineHeight: 1 }}>
                        #{order.orderNumber}
                      </Typography>
                      {fresh && (
                        <Chip
                          size='small'
                          color='warning'
                          icon={<FiberNewIcon />}
                          label='NEU'
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                    </Stack>
                    <Chip
                      size='small'
                      color={isReady ? 'success' : 'warning'}
                      label={STATUS_LABEL[order.status] || order.status}
                    />
                  </Stack>

                  <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                    <Chip
                      size='small'
                      variant='outlined'
                      label={order.tableNumber ? `Tisch ${order.tableNumber}` : 'Abholung'}
                    />
                    {order.locationCode && (
                      <Chip size='small' variant='outlined' label={order.locationCode} />
                    )}
                    {order.hasPizza && (
                      <Chip size='small' color='secondary' icon={<LocalPizzaIcon />} label='Pizza' />
                    )}
                    {order.payOnSite ? (
                      <Chip size='small' color='warning' variant='outlined' label='Vor Ort zahlen' />
                    ) : (
                      <Chip size='small' color='success' variant='outlined' label='Bezahlt' />
                    )}
                  </Stack>

                  <Box sx={{ flexGrow: 1 }}>
                    {(order.items || []).map((item, i) => (
                      <Typography
                        key={i}
                        variant='body2'
                        sx={{
                          fontWeight: item.isPizza ? 700 : 400,
                          color: item.isPizza ? 'secondary.main' : 'text.primary',
                        }}
                      >
                        {item.count}× {item.name}
                      </Typography>
                    ))}
                  </Box>

                  <Typography variant='caption' color='text.secondary'>
                    Bestellt {order.orderedAt ? dayjs(order.orderedAt).format('HH:mm:ss') : '—'}
                    {minsAgo !== null ? ` · vor ${minsAgo} Min.` : ''}
                  </Typography>

                  {canUpdate && (
                    <Stack direction='row' spacing={1} sx={{ mt: 0.5 }}>
                      {order.status === 'inPreparation' && (
                        <Button
                          fullWidth
                          variant='contained'
                          color='success'
                          disabled={busyId === String(order._id)}
                          onClick={() => handleStatus(order, 'ready')}
                          sx={{ textTransform: 'none' }}
                        >
                          Abholbereit
                        </Button>
                      )}
                      {order.status === 'ready' && (
                        <Button
                          fullWidth
                          variant='contained'
                          disabled={busyId === String(order._id)}
                          onClick={() => handleStatus(order, 'pickedUp')}
                          sx={{ textTransform: 'none' }}
                        >
                          Abgeholt
                        </Button>
                      )}
                      <Button
                        variant='outlined'
                        color='error'
                        disabled={busyId === String(order._id)}
                        onClick={() => handleStatus(order, 'cancelled')}
                        sx={{ textTransform: 'none' }}
                      >
                        Storno
                      </Button>
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

export default KitchenOrdersBoard;
